import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  Chip,
  Box,
  IconButton,
  LinearProgress,
  Skeleton,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { formatDistanceToNow } from "date-fns";
import { RecentEvaluationsProps, RecentEvaluation } from "../../types";

const RecentEvaluations: React.FC<RecentEvaluationsProps> = ({
  evaluations = [],
  loading = false,
}) => {
  // Function to render status chip
  const renderStatusChip = (
    status: string,
    successRate?: number,
    progress?: number,
  ) => {
    switch (status) {
      case "completed":
        return (
          <Chip
            label={`Success: ${successRate}%`}
            color="success"
            size="small"
            sx={{ minWidth: 90 }}
          />
        );
      case "running":
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              minWidth: 90,
            }}
          >
            <Typography
              variant="caption"
              color="primary"
              sx={{ fontWeight: "medium" }}
            >
              {`${progress}%`}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              color="primary"
              sx={{ width: "100%", mt: 0.5 }}
            />
          </Box>
        );
      case "failed":
        return (
          <Chip
            label="Failed"
            color="error"
            size="small"
            sx={{ minWidth: 90 }}
          />
        );
      case "pending":
        return (
          <Chip
            label="Pending"
            color="default"
            size="small"
            sx={{ minWidth: 90 }}
          />
        );
      default:
        return null;
    }
  };

  // Loading skeletons
  if (loading) {
    return (
      <List>
        {[1, 2, 3, 4, 5].map((item) => (
          <React.Fragment key={item}>
            <ListItem>
              <ListItemText
                primary={<Skeleton width="60%" />}
                secondary={<Skeleton width="40%" />}
              />
              <ListItemSecondaryAction>
                <Skeleton
                  variant="rectangular"
                  width={90}
                  height={24}
                  sx={{ borderRadius: 2 }}
                />
              </ListItemSecondaryAction>
            </ListItem>
            {item < 5 && <Divider component="li" />}
          </React.Fragment>
        ))}
      </List>
    );
  }

  // Empty state
  if (evaluations.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary">
          No evaluations found.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Run your first evaluation to get started.
        </Typography>
      </Box>
    );
  }

  return (
    <List disablePadding>
      {evaluations.map((evaluation: RecentEvaluation, index: number) => (
        <React.Fragment key={evaluation.id}>
          <ListItem
            component={RouterLink}
            to={`/evaluations/${evaluation.id}`}
            sx={{
              textDecoration: "none",
              color: "inherit",
              "&:hover": {
                bgcolor: "action.hover",
              },
            }}
          >
            <ListItemText
              primary={
                <Typography
                  variant="subtitle1"
                  noWrap
                  sx={{ fontWeight: "medium" }}
                >
                  {evaluation.name}
                </Typography>
              }
              secondary={
                <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {`${evaluation.testsCount} tests`}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mx: 1 }}
                  >
                    •
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDistanceToNow(new Date(evaluation.date), {
                      addSuffix: true,
                    })}
                  </Typography>
                </Box>
              }
            />
            <ListItemSecondaryAction>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {renderStatusChip(
                  evaluation.status,
                  evaluation.successRate,
                  evaluation.progress,
                )}
                <Tooltip title="View details">
                  <IconButton
                    component={RouterLink}
                    to={`/evaluations/${evaluation.id}`}
                    edge="end"
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    <ArrowForwardIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </ListItemSecondaryAction>
          </ListItem>
          {index < evaluations.length - 1 && <Divider component="li" />}
        </React.Fragment>
      ))}
    </List>
  );
};

export default RecentEvaluations;
